import React, {FC} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {CancelToken} from '../../../../../../../../src/helpers/asyncCancelHelpers';
import {ResolveDispatchProps} from '../../../../../../../../src/helpers/reduxHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../../../../../src/helpers/reselectHelpers';
import {
  makeGetContactsManagerTableState,
  makeGetContactsManagerTableTokenPaginationData,
  makeGetIsInMultiSelectMode,
  makeGetListsTableGroupIds,
  makeGetSelectedCardGroupIds,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/contactsManagerPanelSelectors';
import {
  contactsManagerFetchListsTableDataAsyncAction,
  contactsManagerSetUpListsTableAction,
  contactsManagerTableAddSelectedCardGroupIdsAction,
  contactsManagerTableCleanupAction,
  contactsManagerTableRemoveSelectedCardGroupIdsAction,
} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableActions';
import {tableStateIsListsTableState} from '../../../../../../../../src/store/panels/contacts/contactsManager/table/contactsManagerTableState';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {navigateToLinkAction} from '../../../../../../../../src/store/stateless/navigationActions';
import {useInitialEffect} from '../../../../../../../shared/helpers/src/react/hooks/useEffectHelpers';
import {Renderer} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {ListsTable} from './listsTable';

/*
 * Props.
 */

interface ListsTableContainerPublicProps {
  renderEmpty: Renderer;
  panelId: ContactsManagerPanelId;
}
type ListsTableContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type ListsTableContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type ListsTableContainerComponentProps = ListsTableContainerPublicProps &
  ListsTableContainerStateProps &
  ListsTableContainerDispatchProps;

/*
 * Selectors.
 */

function makeGetListsTableStatus(
  getContactsManagerTableState: ReturnType<typeof makeGetContactsManagerTableState>,
) {
  return createSelector([getContactsManagerTableState], (tableState) => {
    if (!tableState || !tableStateIsListsTableState(tableState)) {
      return undefined;
    }
    return tableState.status;
  });
}

/*
 * Component.
 */

export const ListsTableContainerComponent: FC<ListsTableContainerComponentProps> = (props) => {
  const {
    renderEmpty,
    cardGroupIds,
    tableStatus,
    paginationData,
    isInMultiSelectMode,
    selectedCardGroupIds,
    onAddSelectedCardGroupIds,
    onRemoveSelectedCardGroupIds,
    onFetchTableDataAsync,
    cleanup,
    setUpListsTable,
    navigateToLink,
  } = props;

  useInitialEffect(() => {
    setUpListsTable();
    // Listen for the component to be unmounted and clean it up.
    return () => {
      cleanup();
    };
  });

  const onReset = () => {
    cleanup();
    setUpListsTable();
  };

  return (
    <ListsTable
      renderEmpty={renderEmpty}
      cardGroupIds={cardGroupIds}
      tableStatus={tableStatus}
      paginationData={paginationData}
      isInMultiSelectMode={isInMultiSelectMode}
      selectedCardGroupIds={selectedCardGroupIds}
      onAddSelectedCardGroupIds={onAddSelectedCardGroupIds}
      onRemoveSelectedCardGroupIds={onRemoveSelectedCardGroupIds}
      onReset={onReset}
      onFetchTableDataAsync={onFetchTableDataAsync}
      navigateToLink={navigateToLink}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ListsTableContainerPublicProps>()(() => {
  const getContactsManagerTableState = makeGetContactsManagerTableState();
  return {
    isInMultiSelectMode: makeGetIsInMultiSelectMode(),
    selectedCardGroupIds: makeGetSelectedCardGroupIds(),
    cardGroupIds: makeGetListsTableGroupIds(getContactsManagerTableState),
    tableStatus: makeGetListsTableStatus(getContactsManagerTableState),
    paginationData: makeGetContactsManagerTableTokenPaginationData(getContactsManagerTableState),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<ListsTableContainerPublicProps>()(({panelId}) => ({
  setUpListsTable: contactsManagerSetUpListsTableAction,
  cleanup: contactsManagerTableCleanupAction,
  navigateToLink: navigateToLinkAction,
  onAddSelectedCardGroupIds: (cardGroupIds: ReadonlyArray<number>, isInMultiSelectMode?: boolean) =>
    contactsManagerTableAddSelectedCardGroupIdsAction(panelId, cardGroupIds, isInMultiSelectMode),
  onRemoveSelectedCardGroupIds: (cardGroupIds: ReadonlyArray<number>) =>
    contactsManagerTableRemoveSelectedCardGroupIdsAction(panelId, cardGroupIds),
  onFetchTableDataAsync: (cancelToken: CancelToken) =>
    contactsManagerFetchListsTableDataAsyncAction(panelId, cancelToken),
}));

export const ListsTableContainer = connect(mapStateToProps, mapDispatchToProps)(ListsTableContainerComponent);
